import React from "react";
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import styled from "styled-components";
import getRandomColor from "src/components/random-color-generator";
import NoDataPlaceholder from "src/components/no-data-placeholder";
import ChartColors from "src/components/chart-colors";
import { resolveObjPathString } from "src/utils/resolve-path";
import timeSplitter from "src/utils/time-splitter";
import { resolveName } from "src/utils/templating";

const TooltipContainer = styled.div`
    background-color: ${({ theme }) => theme.palette.background.tooltip};
    padding: 0.5rem;
    min-width: 10rem;
    align-items: center;
    flex-direction: column;
    border-radius: 6px;
`;

const TooltipTitle = styled.p`
    margin-top: 0;
    margin-bottom: 0.5rem;
    color: ${({ color }) => color};
`;

const TooltipContent = styled.p`
    font-size: 12px;
    margin: 0;
`;

const CustomTooltip = ({ active, payload, tooltipKeys, context }) => {
    if (active && payload && payload.length) {
        return (
            <TooltipContainer role="status" aria-live="assertive">
                {payload.map((entry) => (
                    <>
                        <TooltipTitle color={entry.payload.fill}>{entry.name}</TooltipTitle>
                        {tooltipKeys.map((tooltipKey) => {
                            let value = resolveObjPathString(
                                entry.payload,
                                resolveName(tooltipKey.property, context)
                            );
                            if (tooltipKey.type === "duration") {
                                value = `${timeSplitter(value, "variable-accuracy")} ${timeSplitter(
                                    value,
                                    "text"
                                )}`;
                            }
                            return (
                                <TooltipContent>
                                    {resolveName(tooltipKey.name, context)} : {value}
                                </TooltipContent>
                            );
                        })}
                    </>
                ))}
            </TooltipContainer>
        );
    }
    return null;
};

const TemplatePieChart = ({ data, parameters, context }) => {
    const HandleColors = (index) => {
        return ChartColors[index] || getRandomColor();
    };

    const sortKey = React.useMemo(
        () => resolveName(parameters?.sortKey, context),
        [parameters && parameters.sortKey, context && JSON.stringify(context)]
    );

    const LegendData = () => {
        const maxCharacters = 100;
        let count = 0;
        if (!data || data.length === 0) return [];
        if (data.length === 1) return data;
        const sorted = data.sort(
            (a, b) => resolveObjPathString(b, sortKey) - resolveObjPathString(a, sortKey)
        );
        const filtered = sorted.filter((entry) => {
            if (entry[parameters.nameKey]) {
                count += entry[parameters.nameKey].length;
                return (
                    count < maxCharacters &&
                    count + entry[parameters.nameKey].length <= maxCharacters
                );
            }
            return null;
        });
        return filtered;
    };
    const legends = React.useMemo(LegendData, [
        data && JSON.stringify(data),
        parameters && JSON.stringify(parameters),
    ]);
    const dataKey = React.useMemo(
        () => resolveName(parameters?.dataKey, context),
        [parameters && parameters.dataKey, context && JSON.stringify(context)]
    );

    if (!data || data.length === 0) return <NoDataPlaceholder variant="pie" />;

    return (
        <ResponsiveContainer width="100%" height="100%" debounce={1}>
            <PieChart>
                <Pie
                    data={data}
                    nameKey={parameters.nameKey}
                    dataKey={dataKey}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    stroke="0"
                >
                    {data.map((entry, index) => (
                        <Cell
                            key={`cell-${entry[parameters.nameKey]}`}
                            fill={HandleColors(index)}
                        />
                    ))}
                </Pie>
                <Legend
                    payload={legends.map((item, index) => ({
                        id: item[parameters.nameKey],
                        type: "circle",
                        value: `${item[parameters.nameKey]}`,
                        color: HandleColors(index),
                    }))}
                    wrapperStyle={{ fontSize: "11px" }}
                    verticalAlign="bottom"
                    iconSize={8}
                />
                <Tooltip
                    content={
                        <CustomTooltip tooltipKeys={parameters.tooltipKeys} context={context} />
                    }
                />
            </PieChart>
        </ResponsiveContainer>
    );
};

export default TemplatePieChart;
